import uuid from 'utils/uuid';
import generatorApi from '../api/generator';
import { getOriginalPage, isTextPlaceholder } from 'editor-utils/template';
import TOC from 'editor-utils/toc';
import { extractPhotosFromPages, transformPagesFromServer } from './utils';

/**
 * Generate Table of Contents Data.
 * @param {string} accessToken
 * @param {object} template
 * @param {object} item
 * @param {Array<string>} tocPageIds
 * @param {object} signal
 * @returns {{ data: { pages: Array, photos: Array }}}
 */
const generateTocData = async (accessToken, template, item, tocPageIds = [], signal) => {
  let pages = [...item.pages];
  if (template.cover) {
    pages.shift();
  }

  let startIndex;

  const tocPages = [];
  pages = pages.filter((page, index) => {
    if (tocPageIds.indexOf(page.id) >= 0) {
      if (startIndex == null) {
        startIndex = index;
      }
      tocPages.push(page);
      return false;
    }
    return true;
  });

  if (startIndex == null) {
    startIndex = 0;
  }

  const { data, error } = await generatorApi(accessToken, template).getTocData(
    {
      pages: pages.map((page, pageIndex) => {
        const originalPage = getOriginalPage(template, page, 'pages');

        return {
          id: originalPage.id,
          caption: originalPage.caption,
          placeholders: originalPage.placeholders.reduce((acc, placeholder) => {
            if (!isTextPlaceholder(placeholder)) {
              // skip other placeholders
              return acc;
            }

            if (placeholder.override?.id != null) {
              // skip dynamically added placeholders (TOC handler use only placeholders defined by template)
              return acc;
            }


            if (["name", "children", "birth", "spouse", "marriage", "death", "bio", "sex", "factoid", "year"].some(prefix => placeholder.id.startsWith(prefix))) {
              // skip common useless placeholders, because from these placeholders is not generated TOC
              return acc
            }

            acc.push({
              placeholder_id: placeholder.id,
              value: placeholder.data?.value ?? '',
            });

            return acc;
          }, []),
          _generator: {
            tocSkip: pageIndex < startIndex || originalPage.tocSkip,
          },
        };
      }),
    },
    signal,
  );

  if (error) {
    return {
      error,
    };
  }

  const newTocPhotos = extractPhotosFromPages(template, data);
  const newTocPages = transformPagesFromServer(template, 'pages', item.photos, data).map(
    (newPage, index) => {
      let key = newPage.key || uuid();
      const pageOverride = newPage.override ?? {};
      const originalPlaceholders = [];
      if (tocPages[index]) {
        const { key: originalKey, placeholders, override } = tocPages[index];

        // Filter server generated text
        key = originalKey;
        Object.assign(pageOverride, override);
        placeholders.forEach(placeholder => {
          // TOC pgnum-x and heading-x placeholders are filled dynamically
          if (TOC.isHeadingPlaceholder(placeholder) || TOC.isPageNumberPlaceholder(placeholder)) {
            return;
          }

          if (TOC.isCaptionPlaceholder(placeholder) && !placeholder.data.value) {
            return;
          }

          originalPlaceholders.push(placeholder);
        });
      }

      originalPlaceholders.forEach(originalPlaceholder => {
        const newPlaceholderIndex = newPage.placeholders.findIndex(
          placeholder => placeholder.id === originalPlaceholder.id,
        );
        if (newPlaceholderIndex >= 0) {
          // Update existing placeholders
          newPage.placeholders[newPlaceholderIndex].data = originalPlaceholder.data;
          newPage.placeholders[newPlaceholderIndex].override = originalPlaceholder.override;
          return;
        }

        // Add as a new placeholder
        if (originalPlaceholder.id === 'custom-background') {
          newPage.placeholders.unshift(originalPlaceholder);
        } else {
          newPage.placeholders.push(originalPlaceholder);
        }
      });

      return { ...newPage, key, override: pageOverride };
    },
  );

  return {
    data: {
      pages: newTocPages,
      photos: newTocPhotos,
      startIndex,
    },
  };
};

export default generateTocData;
